import axios from "axios";

// In local development /api is forwarded by the React dev server. Production
// uses the web container's same-origin reverse proxy. An explicit URL remains
// available for deployments that intentionally host the API elsewhere.
const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || "";
export const API = `${BACKEND_URL}/api`;

export const api = axios.create({
  baseURL: API,
  withCredentials: true,
});

// In-memory bearer token (used as fallback when 3rd-party cookies are blocked).
// Never persisted to localStorage to avoid XSS token theft.
let bearerToken = null;
let sessionExpiryRaised = false;

export const SESSION_EXPIRED_EVENT = "epos:session-expired";
export const RETURN_AFTER_LOGIN_KEY = "epos:return-after-login";

export function setAuthToken(token) {
  bearerToken = token;
  if (token) {
    api.defaults.headers.common["Authorization"] = `Bearer ${token}`;
  } else {
    delete api.defaults.headers.common["Authorization"];
  }
}

export function resetSessionExpiry() {
  sessionExpiryRaised = false;
}

api.interceptors.response.use(
  (response) => response,
  (error) => {
    const status = error?.response?.status;
    const requestUrl = String(error?.config?.url || "");
    const isAuthBootstrap = /\/auth\/(?:login|me|logout)(?:\?|$)/.test(requestUrl);
    if (status === 401 && !isAuthBootstrap) {
      setAuthToken(null);
      if (!sessionExpiryRaised && typeof window !== "undefined") {
        sessionExpiryRaised = true;
        window.dispatchEvent(new CustomEvent(SESSION_EXPIRED_EVENT));
      }
    }
    return Promise.reject(error);
  }
);

export function formatApiError(err) {
  if (err?.response?.status === 401) return "Your secure session has expired. Sign in again to continue.";
  const detail = err?.response?.data?.detail;
  if (detail == null) return err?.message || "Something went wrong.";
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail))
    return detail
      .map((e) => (e && typeof e.msg === "string" ? e.msg : JSON.stringify(e)))
      .filter(Boolean)
      .join(" · ");
  if (detail?.msg) return String(detail.msg);
  if (detail?.message) return String(detail.message);
  if (detail?.error) return String(detail.error);
  if (detail?.reason) return String(detail.reason);
  try {
    return JSON.stringify(detail);
  } catch {
    return "Something went wrong.";
  }
}

export function authenticatedFetch(input, init = {}) {
  const headers = new Headers(init.headers || {});
  if (bearerToken && !headers.has("Authorization")) {
    headers.set("Authorization", `Bearer ${bearerToken}`);
  }
  const target = typeof input === "string" && input.startsWith("/api/")
    ? `${BACKEND_URL || ""}${input}`
    : input;
  return fetch(target, { credentials: "include", ...init, headers });
}
